import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, X, Download } from "lucide-react";
import { getDeferredPrompt, clearDeferredPrompt } from "@/lib/pwaInstall";
import { InstallHelp, isStandalone } from "@/components/InstallButton";

const SEEN_KEY = "cph_planner_install_nudge_seen";

// A gentle, one-time nudge on the Planner: reminds trip-planners to keep the
// guide on their home screen for the trip. Shows once, auto-dismisses, and
// never appears if the app is already installed.
export const PlannerInstallNudge = () => {
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(SEEN_KEY) === "1") return;
    } catch {
      /* ignore */
    }

    const showTimer = setTimeout(() => {
      setVisible(true);
      try {
        localStorage.setItem(SEEN_KEY, "1");
      } catch {
        /* ignore */
      }
    }, 2500);

    return () => clearTimeout(showTimer);
  }, []);

  // Auto-dismiss ~9s after it appears (unless the help modal is open).
  useEffect(() => {
    if (!visible || showHelp) return;
    const hideTimer = setTimeout(() => setVisible(false), 9000);
    return () => clearTimeout(hideTimer);
  }, [visible, showHelp]);

  const install = async () => {
    const deferred = getDeferredPrompt();
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      clearDeferredPrompt();
      setVisible(false);
      return;
    }
    setShowHelp(true);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="planner-install-nudge"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed z-[90] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
          >
            <div className="relative bg-white rounded-2xl shadow-brut border border-zinc-200 p-4 pr-10 flex items-start gap-3">
              <button
                data-testid="planner-install-nudge-close"
                onClick={() => setVisible(false)}
                aria-label="Dismiss"
                className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full hover:bg-zinc-100 flex items-center justify-center transition"
              >
                <X size={15} />
              </button>
              <div className="shrink-0 w-10 h-10 rounded-xl bg-[#DF3226] text-white flex items-center justify-center">
                <Smartphone size={20} />
              </div>
              <div className="min-w-0">
                <p className="font-display font-900 tracking-tight text-sm leading-tight">
                  Take this plan with you
                </p>
                <p className="font-body text-xs text-black/55 mt-0.5">
                  Add the guide to your home screen so it&apos;s one tap away in the city.
                </p>
                <button
                  data-testid="planner-install-nudge-btn"
                  onClick={install}
                  className="mt-2.5 inline-flex items-center gap-1.5 font-display font-800 text-xs bg-[#DF3226] text-white px-4 py-2 rounded-full hover:brightness-95 transition"
                >
                  <Download size={14} /> Add to home screen
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showHelp && <InstallHelp onClose={() => setShowHelp(false)} />}
    </>
  );
};
